import { useEffect, useState } from 'react';
import { Download, Loader2, Info } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/Button';
import { cn } from '../ui/utils';
import { apiHelpers, casesApi, type DecisionLogFormat } from '../../lib/api';
import { decisionLogFilename } from './decisionLog';

/** How long the server keeps audit entries (server/models/audit_log.py TTL). */
export const AUDIT_RETENTION_DAYS = 90;

const FORMAT_OPTIONS: { value: DecisionLogFormat; label: string }[] = [
  { value: 'csv', label: 'CSV (spreadsheet)' },
  { value: 'pdf', label: 'PDF' },
];

const INCLUDED = [
  'Review decisions: document, decision, previous value, who, when, and how (manual, bulk, email thread, other)',
  'Searches run in the case, with the number of hits',
  'Search redactions, redaction approvals and rejections',
  'Finalizations, and documents added to or removed from the case',
];

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface DecisionLogDialogProps {
  caseId: string;
  caseName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Export decision log" (Lake Stevens R2-11): a record of what was searched in the
 * case and how each record was judged, for requests that are really eDiscovery.
 * Separate from the Export dialog, which exports the documents themselves.
 */
export function DecisionLogDialog({ caseId, caseName, open, onOpenChange }: DecisionLogDialogProps) {
  const [format, setFormat] = useState<DecisionLogFormat>('csv');
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const { blob } = await casesApi.downloadDecisionLog(caseId, format);
      saveBlob(blob, decisionLogFilename(caseName, format));
      onOpenChange(false);
    } catch (err) {
      setError(apiHelpers.handleError(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !downloading && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export decision log</DialogTitle>
          <DialogDescription>
            What was searched in this case and how each document was judged, oldest first.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Format</div>
            <div
              role="radiogroup"
              aria-label="Decision log format"
              className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50"
            >
              {FORMAT_OPTIONS.map((option) => {
                const selected = format === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={downloading}
                    onClick={() => setFormat(option.value)}
                    className={cn(
                      'px-4 py-1.5 text-sm font-medium rounded-md transition-colors disabled:opacity-50',
                      selected ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="text-sm font-medium text-gray-700">Includes</div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
              {INCLUDED.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          <p className="flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              The log keeps activity for {AUDIT_RETENTION_DAYS} days. Older activity isn&apos;t in the export, so
              export and keep the file if you need the record longer.
            </span>
          </p>

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading}>
            Cancel
          </Button>
          <Button onClick={download} disabled={downloading}>
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {downloading ? 'Exporting…' : 'Export'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
